import {
  type FilterOpProps,
  type PaginateProps,
  type Retryable,
  api,
  cacheShortTimer,
  cacheTimer,
  combinePages,
  thunks,
} from "@app/api";
import {
  type Next,
  type Operation,
  call,
  createAction,
  createSelector,
  delay,
  mdw,
  poll,
  select,
  takeLeading,
} from "@app/fx";
import {
  defaultEntity,
  extractIdFromLink,
  extractResourceNameFromLink,
} from "@app/hal";
import { selectOrganizationSelected } from "@app/organizations";
import {
  appDetailUrl,
  databaseDetailUrl,
  endpointDetailUrl,
  environmentDetailUrl,
  llmKeyDetailUrl,
  settingsAccountIntegrationsAwsDetailUrl,
} from "@app/routes";
import { type WebState, schema } from "@app/schema";
import { capitalize } from "@app/string-utils";
import { tunaEvent } from "@app/tuna";
import type {
  DeployActivityRow,
  DeployApiCtx,
  DeployOperation,
  HalEmbedded,
  LinkResponse,
  OperationStatus,
  OperationType,
  ResourceType,
} from "@app/types";
import { type ServiceScaleProps, selectServiceById } from "../service";

export interface DeployOperationResponse {
  id: number;
  type: string;
  status: OperationStatus;
  created_at: string;
  updated_at: string;
  git_ref: string;
  docker_ref: string;
  container_count: number | null;
  container_size: number | null;
  disk_size: number | null;
  instance_profile: string | null;
  encrypted_env_json_new: string;
  destination_region: string;
  automated: boolean;
  cancelled: boolean;
  aborted: boolean;
  immediate: boolean;
  provisioned_iops: number;
  ebs_volume_type: string;
  encrypted_stack_settings: string;
  user_email: string;
  user_name: string;
  env: { [key: string]: string | null } | null;
  note: string;
  _links: {
    account: LinkResponse;
    code_scan_result: LinkResponse;
    ephemeral_sessions: LinkResponse;
    logs: LinkResponse;
    resource: LinkResponse;
    self: LinkResponse;
    ssh_portal_connections: LinkResponse;
    user: LinkResponse;
  };
  _type: "operation";
}

export const defaultOperationResponse = (
  op: Partial<DeployOperationResponse> = {},
): DeployOperationResponse => {
  const now = new Date().toISOString();
  return {
    id: 0,
    type: "",
    status: "unknown",
    created_at: now,
    updated_at: now,
    git_ref: "",
    docker_ref: "",
    container_count: 0,
    container_size: 0,
    disk_size: 0,
    encrypted_env_json_new: "",
    destination_region: "",
    automated: false,
    cancelled: false,
    aborted: false,
    immediate: false,
    provisioned_iops: 0,
    ebs_volume_type: "",
    encrypted_stack_settings: "",
    instance_profile: "",
    user_name: "",
    user_email: "",
    env: {},
    note: "",
    _links: {
      account: { href: "" },
      code_scan_result: { href: "" },
      ephemeral_sessions: { href: "" },
      logs: { href: "" },
      resource: { href: "" },
      self: { href: "" },
      ssh_portal_connections: { href: "" },
      user: { href: "" },
      ...op._links,
    },
    _type: "operation",
    ...op,
  };
};

const transformOperationType = (
  type: string | undefined | null,
): OperationType => {
  if (!type) return "unknown";
  // TODO: make this more strict?
  return type as OperationType;
};

export const prettyResourceType = (rType: ResourceType): string => {
  if (rType === "llm_key") {
    return "LLM Key";
  }
  if (rType === "vhost") {
    return "Endpoint";
  }
  if (rType === "external_aws_account") {
    return "External AWS Account";
  }
  return capitalize(rType.replace("_", " "));
};

export const deserializeDeployOperation = (
  payload: DeployOperationResponse,
): DeployOperation => {
  return {
    id: `${payload.id}`,
    environmentId: extractIdFromLink(payload._links.account),
    codeScanResultId: extractIdFromLink(payload._links.code_scan_result),
    resourceId: extractIdFromLink(payload._links.resource),
    resourceType: extractResourceNameFromLink(payload._links.resource),
    type: transformOperationType(payload.type),
    status: payload.status,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
    gitRef: payload.git_ref || "",
    dockerRef: payload.docker_ref,
    containerCount: payload.container_count ?? -1,
    containerSize: payload.container_size ?? -1,
    diskSize: payload.disk_size ?? 0,
    instanceProfile: payload.instance_profile || "",
    encryptedEnvJsonNew: payload.encrypted_env_json_new,
    destinationRegion: payload.destination_region,
    cancelled: payload.cancelled,
    aborted: payload.aborted,
    automated: payload.automated,
    immediate: payload.immediate,
    provisionedIops: payload.provisioned_iops,
    ebsVolumeType: payload.ebs_volume_type,
    encryptedStackSettings: payload.encrypted_stack_settings,
    userEmail: payload.user_email,
    userName: payload.user_name,
    env: payload.env || {},
    note: payload.note,
  };
};

export const hasDeployOperation = (a: DeployOperation) => a.id !== "";
export const selectOperationById = schema.operations.selectById;
export const selectOperationsAsList = createSelector(
  schema.operations.selectTableAsList,
  (_: WebState, props: { limit?: number }) => props.limit,
  (ops, limit) =>
    [...ops]
      .sort((a, b) => {
        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      })
      .slice(0, limit),
);

export const selectOperationsByResourceId = createSelector(
  selectOperationsAsList,
  (_: WebState, props: { resourceId: string }) => props.resourceId,
  (ops, resourceId) => ops.filter((op) => op.resourceId === resourceId),
);

export const selectLatestProvisionOps = createSelector(
  selectOperationsAsList,
  (_: WebState, props: { resourceIds: string[] }) => props.resourceIds,
  (ops, resourceIds) => {
    const results: DeployOperation[] = [];
    for (let i = 0; i < resourceIds.length; i += 1) {
      const id = resourceIds[i];
      const op = ops.find((o) => o.resourceId === id && o.type === "provision");
      if (op) {
        results.push(op);
      }
    }
    return results;
  },
);

export const selectOperationsByEnvId = createSelector(
  selectOperationsAsList,
  (_: WebState, p: { envId: string }) => p.envId,
  (ops, envId) => ops.filter((op) => op.environmentId === envId),
);

export const findOperationsByServiceId = (
  ops: DeployOperation[],
  serviceId: string,
) =>
  ops.filter(
    (op) => op.resourceType === "service" && op.resourceId === serviceId,
  );

export const findOperationsByAppId = (ops: DeployOperation[], appId: string) =>
  ops.filter((op) => op.resourceType === "app" && op.resourceId === appId);

export const findOperationsByDbId = (ops: DeployOperation[], dbId: string) =>
  ops.filter((op) => op.resourceType === "database" && op.resourceId === dbId);

export const selectOperationsByServiceId = createSelector(
  selectOperationsAsList,
  (_: WebState, p: { id: string }) => p.id,
  findOperationsByServiceId,
);

export const selectOperationsByAppId = createSelector(
  selectOperationsAsList,
  (_: WebState, p: { appId: string }) => p.appId,
  findOperationsByAppId,
);

export const findLatestSuccessProvisionDbOp = (ops: DeployOperation[]) =>
  ops.find((op) => op.resourceType === "database" && op.status === "succeeded");

export const selectOperationsByDatabaseId = createSelector(
  selectOperationsAsList,
  (_: WebState, p: { dbId: string }) => p.dbId,
  findOperationsByDbId,
);

export const selectLatestOpByDatabaseId = createSelector(
  selectOperationsByDatabaseId,
  (ops) => (ops.length > 0 ? ops[0] : schema.operations.empty),
);

export const selectLatestOpByAppId = createSelector(
  selectOperationsByAppId,
  (ops) =>
    ops.find((op) =>
      ["configure", "provision", "deploy", "deprovision"].includes(op.type),
    ) || schema.operations.empty,
);

export const selectLatestOpByResourceId = createSelector(
  selectOperationsAsList,
  (_: WebState, p: { resourceId: string }) => p.resourceId,
  (_: WebState, p: { resourceType: ResourceType }) => p.resourceType,
  (ops, resourceId, resourceType) =>
    ops.find(
      (op) =>
        op.resourceId === resourceId &&
        op.resourceType === resourceType &&
        [
          "configure",
          "configure_backup_policy",
          "provision",
          "deploy",
          "deprovision",
        ].includes(op.type),
    ) || schema.operations.empty,
);

export const selectLatestSyncOpByExternalAwsAccountId = createSelector(
  selectOperationsAsList,
  (_: WebState, p: { id: string }) => p.id,
  (ops, id) =>
    ops.find(
      (op) =>
        op.resourceId === id &&
        op.resourceType === "external_aws_account" &&
        op.type === "sync",
    ) || schema.operations.empty,
);

export const selectLatestProvisionOp = createSelector(
  selectOperationsByResourceId,
  (_: WebState, p: { resourceType: ResourceType }) => p.resourceType,
  (ops, resourceType) =>
    ops.find(
      (op) => op.type === "provision" && op.resourceType === resourceType,
    ) || schema.operations.empty,
);

export const selectNonFailedScaleOps = createSelector(
  selectOperationsByServiceId,
  (ops) => ops.filter((op) => op.type === "scale" && op.status !== "failed"),
);

export const selectScaleDiff = createSelector(
  selectNonFailedScaleOps,
  (ops): { latest: DeployOperation; prev: DeployOperation } => {
    if (ops.length < 2) {
      return { latest: schema.operations.empty, prev: schema.operations.empty };
    }

    const latest = { ...ops[0] };
    const prev = { ...ops[1] };

    // update previous and latest operation based on what we can find in the op history
    // ops is sorted in descending order based on updated_at so once we have
    // a real value inside `prev` we dont update the field again
    for (let i = 1; i < ops.length; i += 1) {
      const op = ops[i];
      if (prev.containerCount === -1 && op.containerCount !== -1) {
        prev.containerCount = op.containerCount;
      }
      if (prev.containerSize === -1 && op.containerSize !== -1) {
        prev.containerSize = op.containerSize;
      }
      if (prev.diskSize === 0 && op.diskSize !== 0) {
        prev.diskSize = op.diskSize;
      }
      if (prev.instanceProfile === "" && op.instanceProfile !== "") {
        prev.instanceProfile = op.instanceProfile;
      }

      if (latest.containerCount === -1 && op.containerCount !== -1) {
        latest.containerCount = op.containerCount;
      }
      if (latest.containerSize === -1 && op.containerSize !== -1) {
        latest.containerSize = op.containerSize;
      }
      if (latest.diskSize === 0 && op.diskSize !== 0) {
        latest.diskSize = op.diskSize;
      }
      if (latest.instanceProfile === "" && op.instanceProfile !== "") {
        latest.instanceProfile = op.instanceProfile;
      }
    }

    return { prev, latest };
  },
);

export const selectLatestScanOp = createSelector(
  selectOperationsByAppId,
  (ops) => ops.find((op) => op.type === "scan_code") || schema.operations.empty,
);

export const selectLatestConfigureOp = createSelector(
  selectOperationsByAppId,
  (ops) => ops.find((op) => op.type === "configure") || schema.operations.empty,
);

export const findLatestDeployOp = (ops: DeployOperation[]) =>
  ops.find((op) => op.type === "deploy");

export const findLatestDbProvisionOp = (ops: DeployOperation[]) =>
  ops.find((op) => op.resourceType === "database" && op.type === "provision");

export const selectLatestDeployOp = createSelector(
  selectOperationsByAppId,
  (ops) => ops.find((op) => op.type === "deploy") || schema.operations.empty,
);

export const selectLatestDeployOpWithCodeScan = createSelector(
  selectOperationsByAppId,
  (ops) =>
    ops.find((op) => op.type === "deploy" && op.codeScanResultId !== "") ||
    schema.operations.empty,
);

export const findLatestSuccessDeployOp = (ops: DeployOperation[]) =>
  ops.find((op) => op.type === "deploy" && op.status === "succeeded");

export const selectLatestSuccessDeployOpByEnvId = createSelector(
  selectOperationsByEnvId,
  (ops) => findLatestSuccessDeployOp(ops) || schema.operations.empty,
);

export const findLatestSuccessScanOp = (ops: DeployOperation[]) =>
  ops.find((op) => op.type === "scan_code" && op.status === "succeeded");

export const selectLatestSuccessScanOp = createSelector(
  selectOperationsByAppId,
  (ops) => findLatestSuccessScanOp(ops) || schema.operations.empty,
);

interface EnvIdProps {
  envId: string;
}

interface EnvOpProps extends PaginateProps, EnvIdProps {}

export const fetchEnvOperations = api.get<EnvOpProps>(
  "/accounts/:envId/operations?page=:page&per_page=250",
  { supervisor: takeLeading },
);

export const fetchAllEnvOps = thunks.create<EnvIdProps>(
  "fetch-all-env-ops",
  { supervisor: cacheShortTimer() },
  combinePages(fetchEnvOperations, { max: 2 }),
);

function* paginateOps(
  ctx: DeployApiCtx<
    any,
    HalEmbedded<{ operations: DeployOperationResponse[] }>
  >,
  next: Next,
) {
  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const ids = ctx.json.value._embedded.operations.map(
    (entity) => `${entity.id}`,
  );
  const paginatedData = { ...ctx.json.value, _embedded: { operations: ids } };
  yield* schema.update(schema.cache.add({ [ctx.key]: paginatedData }));
}

export const emptyFilterProps: FilterOpProps = {
  resourceType: "",
  operationType: "",
  operationStatus: "",
  startDate: "",
  endDate: "",
};

export const cancelOrgOperationsPoll = createAction("cancel-org-ops-poll");
export const pollOrgOperations = api.get<{ orgId: string }>(
  "/organizations/:orgId/operations",
  { supervisor: poll(10 * 1000, `${cancelOrgOperationsPoll}`) },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByOrgId({
      page: 1,
      id: ctx.payload.orgId,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

export const pollOrgOperationsByTypes = api.get<
  { orgId: string; operationTypes: string[] },
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  ["/organizations/:orgId/operations?page=1", "poll-by-types"],
  { supervisor: poll(10 * 1000, `${cancelOrgOperationsPoll}`) },
  function* (ctx, next) {
    const operationType = ctx.payload.operationTypes.join(",");
    const urlParam = ctxToUrlQuery({ ...emptyFilterProps, operationType });

    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });

    yield* next();

    // Write the polled response into the same cache key the list page uses (page 1).
    const action = fetchOperationsByOrgId({
      page: 1,
      id: ctx.payload.orgId,
      ...emptyFilterProps,
      operationType,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

function ctxToUrlQuery(payload: FilterOpProps) {
  const urlParam = new URLSearchParams();
  if (payload.resourceType) {
    urlParam.set("resource_type", payload.resourceType);
  }
  if (payload.operationType) {
    urlParam.set("type", payload.operationType);
  }
  if (payload.operationStatus) {
    urlParam.set("status", payload.operationStatus);
  }
  if (payload.startDate) {
    urlParam.set("start_date", payload.startDate);
  }
  if (payload.endDate) {
    urlParam.set("end_date", payload.endDate);
  }
  const query = urlParam.toString();
  return query ? `&${query}` : "";
}

export const fetchOperationsByOrgId = api.get<
  { id: string } & PaginateProps & FilterOpProps
>(
  "/organizations/:id/operations?page=:page",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);

    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const fetchOperationsByEnvId = api.get<
  { id: string } & PaginateProps & FilterOpProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  "/accounts/:id/operations?page=:page",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);
    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const fetchOperationsByExternalAwsAccountId = api.get<
  { id: string } & PaginateProps & FilterOpProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  "/external_aws_accounts/:id/operations?page=:page",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);
    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const cancelExternalAwsAccountOpsPoll = createAction(
  "cancel-external-aws-account-ops-poll",
);
export const pollExternalAwsAccountOperations = api.get<{ id: string }>(
  ["/external_aws_accounts/:id/operations", "poll"],
  { supervisor: poll(10 * 1000, `${cancelExternalAwsAccountOpsPoll}`) },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByExternalAwsAccountId({
      page: 1,
      id: ctx.payload.id,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

export const cancelEnvOperationsPoll = createAction("cancel-env-ops-poll");
export const pollEnvOperations = api.get<
  EnvIdProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  ["/accounts/:envId/operations?page=1", "poll"],
  { supervisor: poll(10 * 1000, `${cancelEnvOperationsPoll}`) },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByEnvId({
      page: 1,
      id: ctx.payload.envId,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

export const fetchOperationsByAppId = api.get<
  { id: string } & PaginateProps & FilterOpProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  "/apps/:id/operations?page=:page&with_services=true",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);
    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const cancelAppOpsPoll = createAction("cancel-app-ops-poll");
export const pollAppOperations = api.get<{ id: string }>(
  ["/apps/:id/operations?with_services=true", "poll"],
  {
    supervisor: poll(10 * 1000, `${cancelAppOpsPoll}`),
  },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByAppId({
      page: 1,
      id: ctx.payload.id,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

export const fetchOperationsByDatabaseId = api.get<
  { id: string } & PaginateProps & FilterOpProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  "/databases/:id/operations?page=:page&with_services=true",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);
    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const cancelDatabaseOpsPoll = createAction("cancel-db-ops-poll");
export const pollDatabaseOperations = api.get<{ id: string }>(
  ["/databases/:id/operations?with_services=true", "poll"],
  { supervisor: poll(10 * 1000, `${cancelDatabaseOpsPoll}`) },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByDatabaseId({
      page: 1,
      id: ctx.payload.id,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

export const fetchOperationsByServiceId = api.get<
  { id: string } & PaginateProps & FilterOpProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  "/services/:id/operations?page=:page",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);
    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const cancelServicesOpsPoll = createAction("cancel-services-ops-poll");
export const pollServiceOperations = api.get<{ id: string }>(
  ["/services/:id/operations", "poll"],
  { supervisor: poll(10 * 1000, `${cancelServicesOpsPoll}`) },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByServiceId({
      page: 1,
      id: ctx.payload.id,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

export const fetchOperationsByEndpointId = api.get<
  { id: string } & PaginateProps & FilterOpProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  "/vhosts/:id/operations?page=:page",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);
    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const cancelEndpointOpsPoll = createAction("cancel-enp-ops-poll");
export const pollEndpointOperations = api.get<{ id: string }>(
  ["/vhosts/:id/operations", "poll"],
  { supervisor: poll(10 * 1000, `${cancelEndpointOpsPoll}`) },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByEndpointId({
      page: 1,
      id: ctx.payload.id,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

export const fetchOperationsByLlmKeyId = api.get<
  { id: string } & PaginateProps & FilterOpProps,
  HalEmbedded<{ operations: DeployOperationResponse[] }>
>(
  "/llm_keys/:id/operations?page=:page",
  { supervisor: cacheTimer() },
  function* (ctx, next) {
    const urlParam = ctxToUrlQuery(ctx.payload);
    ctx.request = ctx.req({
      url: `${ctx.req().url}${urlParam}`,
    });
    yield* paginateOps(ctx, next);
  },
);

export const cancelLlmKeyOpsPoll = createAction("cancel-llm-key-ops-poll");
export const pollLlmKeyOperations = api.get<{ id: string }>(
  ["/llm_keys/:id/operations", "poll"],
  { supervisor: poll(10 * 1000, `${cancelLlmKeyOpsPoll}`) },
  function* (ctx, next) {
    yield* next();
    const action = fetchOperationsByLlmKeyId({
      page: 1,
      id: ctx.payload.id,
      ...emptyFilterProps,
    });
    yield* paginateOps({ ...ctx, key: action.payload.key }, function* () {});
  },
);

// fetchOperationLogs - will attempt to fetch even if on deleted resource
// since this is chained. this may retry may times in sequence to verify/validate
export const fetchOperationLogs = api.get<{ id: string } & Retryable>(
  "/operations/:id/logs?with_deleted=true",
  [
    function* (ctx, next) {
      ctx.cache = true;
      ctx.bodyType = "text";

      yield* next();

      if (!ctx.json.ok) {
        return;
      }

      const url = ctx.json.value;
      const response = yield* call(() => fetch(url));
      const message = yield* call(() => response.text());

      if (!response.ok) {
        ctx.json = {
          ok: false,
          error: { message },
        };
        return;
      }
      // overwrite the URL provided by the API with the actual logs
      // so we can just fetch the data in a single endpoint
      ctx.json.value = message;
    },
    mdw.fetchRetry(),
  ],
);

export const fetchOperationById = api.get<
  { id: string; withDeleted?: boolean },
  DeployOperationResponse
>("/operations/:id", function* (ctx, next) {
  if (ctx.payload.withDeleted) {
    const baseUrl = ctx.req().url;
    const url = new URL(baseUrl);
    url.searchParams.set("with_deleted", "true");
    ctx.request = ctx.req({ url: url.toString() });
  }
  yield* next();
});

export const cancelRunningOperation = api.put<
  { id: string },
  DeployOperationResponse
>("/operations/:id", function* (ctx, next) {
  ctx.request = ctx.req({ body: JSON.stringify({ cancelled: true }) });
  yield* next();

  if (ctx.json.ok) return;

  const msg = ctx.json.error?.message || "";
  if (
    msg.includes("Cancelation already requested") ||
    msg.includes("Operation already")
  ) {
    ctx.loader = { meta: { alreadyCancelled: true } };
  }
});

export const cancelOpByIdPoll = createAction("cancel-op-by-id-poll");
export const pollOperationById = api.get<
  { id: string; withDeleted?: boolean },
  DeployOperationResponse
>(
  ["/operations/:id", "poll"],
  {
    supervisor: poll(3 * 1000, `${cancelOpByIdPoll}`),
  },
  function* (ctx, next) {
    if (ctx.payload.withDeleted) {
      const baseUrl = ctx.req().url;
      const url = new URL(baseUrl);
      url.searchParams.set("with_deleted", "true");
      ctx.request = ctx.req({ url: url.toString() });
    }
    yield* next();
  },
);

export const scaleService = api.post<
  ServiceScaleProps,
  DeployOperationResponse
>(["/services/:id/operations", "scale"], function* (ctx, next) {
  const {
    id,
    containerCount,
    containerProfile,
    containerSize,
    recId = "",
  } = ctx.payload;
  const org = yield* select(selectOrganizationSelected);
  const service = yield* select((s: WebState) => selectServiceById(s, { id }));
  const body = {
    type: "scale",
    id,
    container_count: containerCount,
    instance_profile: containerProfile,
    container_size: containerSize,
  };
  ctx.request = ctx.req({ body: JSON.stringify(body) });
  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  if (recId !== "") {
    const recommendation = yield* select((s: WebState) =>
      schema.manualScaleRecommendations.selectById(s, { id: recId }),
    );
    tunaEvent(
      "scale-service-with-recommendation",
      JSON.stringify({
        orgId: org.id,
        orgName: org.name,
        serviceId: id,
        opId: ctx.json.value.id,
        costSavings: recommendation.costSavings,
        curContainerProfile: service.instanceClass,
        curContainersize: service.containerMemoryLimitMb,
        recContainerProfile: recommendation.recommendedInstanceClass,
        recContainerSize: recommendation.recommendedContainerMemoryLimitMb,
        appliedContainerProfile: containerProfile,
        appliedContainerSize: containerSize,
      }),
    );
  }

  const opId = ctx.json.value.id;
  ctx.loader = {
    message: `Scale service operation queued (operation ID: ${opId})`,
    meta: { opId: `${opId}` },
  };
});

type WaitResult = DeployOperation | undefined;

export function* waitForOperation({
  id,
  wait = 3 * 1000,
  skipFetch = false,
}: {
  id: string;
  wait?: number;
  skipFetch?: boolean;
}): Operation<WaitResult> {
  while (true) {
    if (skipFetch) {
      const op = yield* select((s: WebState) => selectOperationById(s, { id }));
      if (op.status === "succeeded" || op.status === "failed") {
        return op;
      }
    } else {
      const ctx = yield* call(() =>
        fetchOperationById.run(fetchOperationById({ id })),
      );

      if (ctx.json.ok) {
        if (
          ctx.json.value.status === "succeeded" ||
          ctx.json.value.status === "failed"
        ) {
          return deserializeDeployOperation(ctx.json.value);
        }
      } else {
        const op = yield* select((s: WebState) =>
          selectOperationById(s, { id }),
        );
        // When a deprovision happens and it is successful, the API will
        // eventually return a 404 because the operation is "soft" deleted.
        // As a result, we "hack" the FE by checking for this edge case
        // and then marking the operation as successful.
        // This is okay because after the user refreshes the page, the
        // operation will disappear.
        if (op.type === "deprovision" && ctx.response?.status === 404) {
          yield* schema.update(
            schema.operations.patch({
              [op.id]: { id: op.id, status: "succeeded" },
            }),
          );
          return { ...op, status: "succeeded" };
        }
      }
    }

    yield* delay(wait);
  }
}

export const opEntities = {
  operation: defaultEntity({
    id: "operation",
    deserialize: deserializeDeployOperation,
    save: schema.operations.add,
  }),
};

export const createReadableStatus = (status: OperationStatus): string => {
  return capitalize(status);
};

export const getResourceUrl = ({
  resourceId,
  resourceType,
  url,
}: Pick<DeployActivityRow, "resourceId" | "resourceType" | "url">) => {
  switch (resourceType) {
    case "app":
      return appDetailUrl(resourceId);
    case "llm_key":
      return llmKeyDetailUrl(resourceId);
    case "database":
      return databaseDetailUrl(resourceId);
    case "environment":
      return environmentDetailUrl(resourceId);
    case "vhost":
      return endpointDetailUrl(resourceId);
    case "metric_drain":
    case "log_drain":
    case "service":
      return url;
    case "external_aws_account":
      return settingsAccountIntegrationsAwsDetailUrl(resourceId);
    default:
      return "";
  }
};
